import { RecentDocumentItemViewModel } from "client/Api/Documents/SharePoint/common";
import { GridItem } from "./SharePointRecentlyModified";
import { AlertManager } from "client/Hooks";
import { UserEditorPreference } from "client_legacy/Documents/officeDocumentsOpenPreference";
import {
	getRecentDocumentSelectionActions,
	RecentDocumentAction,
	RecentDocumentActionAndOthers,
	RecentDocumentActionContext
} from "./actions";
import { useMemo } from "react";
import { getSharePointListActions } from "../actions";
import { ActionsWithContext } from "client_legacy/ndaw/actions";
import { ToolbarAction } from "client/Components";

export type RecentDocumentActionHandlers = {
	openImageDialog: (item: RecentDocumentItemViewModel) => void;
	enableSelectMode: () => void;
	disableSelectMode: () => void;
	deselectAll: () => void;
	selectAll: () => void;
};

export type RecentDocumentActionOptions = {
	selectedItems: readonly GridItem[];
	isSharingViaLinkEnabled: boolean;
	isMobile: boolean;
	isInSelectMode: boolean;
	allItems: readonly GridItem[];
	alertManager: AlertManager;
	editorPreference: UserEditorPreference | undefined;
	handlers: RecentDocumentActionHandlers;
};

export function useRecentDocumentActions(
	options: RecentDocumentActionOptions
): [
	actionContext: RecentDocumentActionContext,
	selectionActions: readonly RecentDocumentAction[],
	listActions: readonly RecentDocumentActionAndOthers[]
] {
	const {
		isInSelectMode,
		isMobile,
		isSharingViaLinkEnabled,
		selectedItems,
		allItems,
		alertManager,
		editorPreference,
		handlers
	} = options;

	const {
		deselectAll,
		disableSelectMode,
		enableSelectMode,
		openImageDialog,
		selectAll
	} = handlers;

	const actionContext = useMemo(
		(): RecentDocumentActionContext => ({
			isInSelectMode,
			isMobile,
			isSharingViaLinkEnabled,
			selection: selectedItems.map(x => x.model)
		}),
		[
			isInSelectMode,
			isMobile,
			isSharingViaLinkEnabled,
			selectedItems
		]
	);

	const selectionActions = useMemo(
		(): readonly RecentDocumentAction[] => getRecentDocumentSelectionActions({
			alertManager,
			editorPreference,
			handlers: {
				openImageDialog
			}
		}),
		[
			alertManager,
			editorPreference,
			openImageDialog
		]
	);

	const listActions = useMemo(
		(): readonly RecentDocumentActionAndOthers[] => getSharePointListActions<RecentDocumentItemViewModel, RecentDocumentActionContext>(
			allItems.map(x => x.model),
			{
				deselectAll,
				disableSelectMode,
				enableSelectMode,
				selectAll
			}
		),
		[
			allItems,
			deselectAll,
			disableSelectMode,
			enableSelectMode,
			selectAll
		]
	);

	return [
		actionContext,
		selectionActions,
		listActions
	];
}

export type RecentDocumentActionsWithContext = ActionsWithContext<
	RecentDocumentActionContext,
	ToolbarAction<RecentDocumentActionContext>
>;